import React, { useEffect, useMemo, useState } from "react";
import { Button } from "reactstrap";
import { toast } from "react-toastify";
import {
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import {
	getMonitoringSearch,
	syncMonitoringSearch,
} from "../../../../services/monitoringApi";
import { INLINE_ROWS, SEARCH_ENGINES, SEARCH_SURFACES } from "./trafficShape";
import { compareLabel, fmtDate, fmtDateTime, fmtNum } from "./trafficUtils";
import {
	TrafficCard,
	TrafficKpiCard,
	TrafficSourceBadge,
	TrafficTableTh,
} from "./TrafficKpiCard";
import TrafficListDialog from "./TrafficListDialog";
import UrlCopyCell from "./UrlCopyCell";

const ENGINE_CONNECT_HINT = {
	gsc: "Google Search Console",
	bing: "Bing Webmaster Tools",
};

const ENGINE_VENDOR = {
	gsc: "Google",
	bing: "Bing",
};

const ENGINE_SYNC_TOAST = {
	gsc: "Google Search Console sync queued",
	bing: "Bing Webmaster sync queued",
};

function PositionKpi({ label, kpi, connected, connectHint, engine, hint }) {
	return (
		<TrafficCard label={label} hint={connected ? hint : undefined}>
			{!connected ? (
				<div className="monitoring__ai-traffic-connect-prompt">
					Connect <strong>{connectHint}</strong> to measure this.
				</div>
			) : (
				<>
					<div className="monitoring__ai-traffic-kpi-value-row">
						<span className="monitoring__ai-traffic-kpi-value">
							{kpi?.value == null ? "—" : fmtNum(kpi.value)}
						</span>
						<TrafficSourceBadge source={kpi?.source ?? engine} />
						{kpi?.improvement != null && (
							<span
								className={`monitoring__ai-traffic-delta ${
									kpi.improvement >= 0
										? "monitoring__ai-traffic-delta--up"
										: "monitoring__ai-traffic-delta--down"
								}`}
							>
								{kpi.improvement >= 0 ? "▲" : "▼"}{" "}
								{Math.abs(kpi.improvement).toFixed(1)}
							</span>
						)}
					</div>
				</>
			)}
		</TrafficCard>
	);
}

function SearchTrendChart({ series, engine }) {
	if (!series?.length || series.length < 2) {
		return (
			<p className="monitoring__ai-traffic-chart-empty mb-0">
				Not enough days yet to draw a trend.
			</p>
		);
	}
	const maxClicks = Math.max(...series.map((d) => d.clicks ?? 0), 1);
	const maxImpr = Math.max(...series.map((d) => d.impressions ?? 0), 1);
	const swatchClass =
		engine === "bing"
			? "monitoring__ai-traffic-legend-swatch--bing"
			: "monitoring__ai-traffic-legend-swatch--gsc";
	return (
		<div className="monitoring__ai-traffic-search-trend">
			<ResponsiveContainer width="100%" height={180}>
				<LineChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
					<XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 10 }} />
					<YAxis yAxisId="clicks" tick={{ fontSize: 10 }} width={32} />
					<YAxis yAxisId="impr" orientation="right" tick={{ fontSize: 10 }} width={40} />
					<Tooltip labelFormatter={fmtDate} />
					<Line
						yAxisId="impr"
						type="monotone"
						dataKey="impressions"
						stroke="#059669"
						strokeWidth={2}
						dot={false}
						name="Impressions"
					/>
					<Line
						yAxisId="clicks"
						type="monotone"
						dataKey="clicks"
						stroke="#4f46e5"
						strokeWidth={2}
						dot={false}
						name="Clicks"
					/>
				</LineChart>
			</ResponsiveContainer>
			<div className="monitoring__ai-traffic-chart-legend">
				<span>
					<i className={`monitoring__ai-traffic-legend-swatch ${swatchClass}`} />
					Impressions (peak {fmtNum(maxImpr)})
				</span>
				<span>
					<i className="monitoring__ai-traffic-legend-swatch monitoring__ai-traffic-legend-swatch--clicks" />
					Clicks (peak {fmtNum(maxClicks)})
				</span>
				<span className="fst-italic">Scaled independently — hover for exact values.</span>
			</div>
		</div>
	);
}

function BarRow({ label, value, total, barClass, note }) {
	const pct = total ? (value / total) * 100 : 0;
	return (
		<div className="monitoring__ai-traffic-status-row">
			<div className="monitoring__ai-traffic-status-row-head">
				<span>{label}</span>
				<span>
					{pct.toFixed(1)}%{" "}
					<span className="text-muted">
						({fmtNum(value)}
						{note ?? ""})
					</span>
				</span>
			</div>
			<div className="monitoring__ai-traffic-status-bar-track">
				<div
					className={`monitoring__ai-traffic-status-bar-fill ${barClass}`}
					style={{ width: `${Math.min(pct, 100)}%` }}
				/>
			</div>
		</div>
	);
}

function buildSearchParams(range, compare, surface, engine, scope) {
	return {
		start: range.start,
		end: range.end,
		compareStart: compare?.start,
		compareEnd: compare?.end,
		searchType: surface,
		engine,
		connectionId: scope || undefined,
	};
}

export default function SearchPanel({ websiteId, range, compare }) {
	const [engine, setEngine] = useState("gsc");
	const [surface, setSurface] = useState("web");
	const [scope, setScope] = useState("");
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [syncBusy, setSyncBusy] = useState(false);
	const [syncQueued, setSyncQueued] = useState(false);
	const [queriesOpen, setQueriesOpen] = useState(false);
	const [pagesOpen, setPagesOpen] = useState(false);

	useEffect(() => {
		let cancelled = false;
		async function load() {
			setLoading(true);
			const res = await getMonitoringSearch(
				websiteId,
				buildSearchParams(range, compare, surface, engine, scope)
			);
			if (cancelled) return;
			setData(res.ok ? res.search : null);
			setLoading(false);
		}
		load();
		return () => {
			cancelled = true;
		};
	}, [
		websiteId,
		range.start,
		range.end,
		compare?.start,
		compare?.end,
		surface,
		engine,
		scope,
	]);

	function handleEngineChange(nextEngine) {
		setEngine(nextEngine);
		setScope("");
		if (nextEngine === "bing") {
			setSurface("web");
		}
	}

	async function handleSync() {
		if (syncBusy) return;
		setSyncBusy(true);
		const res = await syncMonitoringSearch(websiteId, { engine });
		setSyncBusy(false);
		if (!res.ok) {
			toast.error(res.message || "Sync failed");
			return;
		}
		toast.success(ENGINE_SYNC_TOAST[engine] ?? "Search sync queued");
		setSyncQueued(true);
		setTimeout(() => {
			getMonitoringSearch(
				websiteId,
				buildSearchParams(range, compare, surface, engine, scope)
			).then((r) => {
				if (r.ok) {
					setData(r.search);
					setSyncQueued(false);
				}
			});
		}, 8000);
	}

	const availableEngines = useMemo(() => {
		const keys = data?.engines?.length ? data.engines : SEARCH_ENGINES.map((e) => e.key);
		return SEARCH_ENGINES.filter((e) => keys.includes(e.key));
	}, [data?.engines]);

	const engineProperties = useMemo(
		() => (data?.properties || []).filter((p) => p.connector_key === engine),
		[data?.properties, engine]
	);

	const engineConnection = useMemo(
		() => (data?.connections || []).find((c) => c.source === engine),
		[data?.connections, engine]
	);

	const hasEngineProperty = engineProperties.length > 0;
	const connectHint = ENGINE_CONNECT_HINT[engine] ?? "search console";
	const vendorName = ENGINE_VENDOR[engine] ?? "Search";
	const uneven = !!data?.comparison?.uneven;
	const cmpLabel = compareLabel(data?.comparison);
	const inline = data?.inline_rows ?? INLINE_ROWS;
	const listsTruncated = !!data?.lists_truncated;
	const activeSurface = SEARCH_SURFACES.find((s) => s.key === surface);
	const visibleSurfaces =
		engine === "bing" ? SEARCH_SURFACES.filter((s) => s.key === "web") : SEARCH_SURFACES;
	const showQueriesIntent = (data?.top_queries || []).some((q) => q.intent != null);

	if (loading && !data) {
		return (
			<p className="monitoring__ai-traffic-empty mb-0">Loading search performance…</p>
		);
	}

	const kpis = data?.kpis || {};
	const brand = data?.brand_split;
	const connected = data?.connected;

	return (
		<div className="monitoring__ai-traffic-panel">
			<div className="monitoring__ai-traffic-search-toolbar">
				<div className="monitoring__ai-traffic-search-toolbar-left">
					<span className="monitoring__ai-traffic-filter-label">Engine</span>
					<div
						className="monitoring__ai-traffic-engine-group"
						role="group"
						aria-label="Search engine"
					>
						{availableEngines.map((e) => (
							<button
								key={e.key}
								type="button"
								className={`monitoring__ai-traffic-pill${
									engine === e.key ? " monitoring__ai-traffic-pill--active" : ""
								}`}
								aria-pressed={engine === e.key}
								onClick={() => handleEngineChange(e.key)}
							>
								{e.label}
							</button>
						))}
					</div>
					<span className="monitoring__ai-traffic-filter-label">Surface</span>
					<div
						className="monitoring__ai-traffic-surface-group"
						role="group"
						aria-label="Search surface"
					>
						{visibleSurfaces.map((s) => (
							<button
								key={s.key}
								type="button"
								className={`monitoring__ai-traffic-pill${
									surface === s.key ? " monitoring__ai-traffic-pill--active" : ""
								}`}
								aria-pressed={surface === s.key}
								onClick={() => setSurface(s.key)}
							>
								{s.label}
							</button>
						))}
					</div>
				</div>
				<div className="monitoring__ai-traffic-search-toolbar-right">
					{engineProperties.length > 1 && (
						<select
							value={scope}
							onChange={(e) => setScope(e.target.value)}
							className="form-select form-select-sm monitoring__ai-traffic-scope-select"
							aria-label={`${vendorName} property`}
						>
							<option value="">All properties (primary)</option>
							{engineProperties.map((p) => (
								<option key={p.connection_id} value={p.connection_id}>
									{p.site_url || p.connection_id}
								</option>
							))}
						</select>
					)}
					{engineProperties.length === 1 && engineProperties[0].site_url && (
						<span className="monitoring__ai-traffic-property-badge">
							{engineProperties[0].site_url}
						</span>
					)}
					<Button
						size="sm"
						color="primary"
						className="monitoring__action-btn monitoring__action-btn--primary"
						disabled={syncBusy}
						onClick={handleSync}
					>
						{syncBusy ? "Syncing…" : syncQueued ? "Sync queued" : "Sync now"}
					</Button>
				</div>
			</div>

			<div
				className={`monitoring__ai-traffic-panel-content${
					loading ? " monitoring__ai-traffic-panel-content--busy" : ""
				}`}
			>
				{data && (
					<>
						{!connected && (
							<div className="monitoring__ai-traffic-banner monitoring__ai-traffic-banner--amber">
								<strong>
									{connectHint} is not connected for this website.
								</strong>{" "}
								Connect it from <em>Manage sources</em> to see whether{" "}
								{vendorName} keeps and surfaces the pages AI engines cite.
							</div>
						)}

						{connected && !hasEngineProperty && (
							<div className="monitoring__ai-traffic-banner monitoring__ai-traffic-banner--amber">
								<strong>{connectHint} is not connected.</strong> Connect it from{" "}
								<em>Manage sources</em> to view {vendorName} search performance.
							</div>
						)}

						{connected && hasEngineProperty && !engineConnection && (
							<div className="monitoring__ai-traffic-banner monitoring__ai-traffic-banner--amber">
								<strong>Connected — sync to load data.</strong> No synced days yet
								for {vendorName}. Use Sync now to backfill.
							</div>
						)}

						{connected && hasEngineProperty && engine === "gsc" && (
							<div className="monitoring__ai-traffic-info-banner">
								<strong>Search Console days are Google&apos;s.</strong> Buckets are
								Pacific time with no hour dimension, and{" "}
								{data.latency_note?.toLowerCase?.() ||
									"data finalizes 2–3 days back."}
								{data.data_through && (
									<>
										{" "}
										Data through {fmtDate(data.data_through)}
										{data.finalized_through &&
										data.finalized_through !== data.data_through
											? ` (finalized through ${fmtDate(data.finalized_through)})`
											: ""}
										.
									</>
								)}{" "}
								Charts that overlay this with Cloudflare or GA4 are offset by up
								to a few hours.
								{data.last_sync && (
									<> Last synced {fmtDateTime(data.last_sync)}.</>
								)}
							</div>
						)}

						{connected && hasEngineProperty && engine === "bing" && (
							<div className="monitoring__ai-traffic-info-banner">
								<strong>Bing Webmaster days are Microsoft&apos;s.</strong>{" "}
								{data.latency_note?.toLowerCase?.() ||
									"data finalizes a few days back."}
								{data.day_bucketing && (
									<> Bucketing: {data.day_bucketing.replace(/_/g, " ")}.</>
								)}
								{data.data_through && (
									<> Data through {fmtDate(data.data_through)}.</>
								)}
								{data.last_sync && (
									<> Last synced {fmtDateTime(data.last_sync)}.</>
								)}
							</div>
						)}

						{engineConnection && (
							<p className="monitoring__ai-traffic-kpi-hint mb-0">
								{engineConnection.days} day{engineConnection.days === 1 ? "" : "s"}{" "}
								synced
								{engineConnection.first_date && engineConnection.last_date
									? ` (${fmtDate(engineConnection.first_date)} – ${fmtDate(engineConnection.last_date)})`
									: ""}
							</p>
						)}

						{uneven && data.comparison && data.range && (
							<div className="monitoring__ai-traffic-banner monitoring__ai-traffic-banner--amber">
								<strong>Comparison covers a different number of days.</strong> This
								range has {data.range.days_covered} day(s) of data, the comparison
								has {data.comparison.days_covered}. Deltas therefore show the{" "}
								<strong>per-day rate</strong>, which is comparable, rather than raw
								totals.
							</div>
						)}

						{(data.warnings || []).map((w) => (
							<div
								key={w}
								className="monitoring__ai-traffic-banner monitoring__ai-traffic-banner--amber"
							>
								{w}
							</div>
						))}

						<div className="monitoring__ai-traffic-kpi-grid">
							<TrafficKpiCard
								label={`Organic clicks (${data.range?.days ?? ""}d)`}
								kpi={kpis.clicks}
								connected={connected && hasEngineProperty}
								connectHint={connectHint}
								uneven={uneven}
								compareLabel={cmpLabel}
							/>
							<TrafficKpiCard
								label={`Impressions (${data.range?.days ?? ""}d)`}
								kpi={kpis.impressions}
								connected={connected && hasEngineProperty}
								connectHint={connectHint}
								uneven={uneven}
								compareLabel={cmpLabel}
							/>
							<TrafficKpiCard
								label="Organic CTR"
								kpi={kpis.ctr}
								unit="%"
								valueDecimals={2}
								connected={connected && hasEngineProperty}
								connectHint={connectHint}
								uneven={uneven}
								compareLabel={cmpLabel}
							/>
							<PositionKpi
								label="Average position"
								kpi={kpis.position}
								engine={engine}
								connected={connected && hasEngineProperty}
								connectHint={connectHint}
								hint={kpis.position?.weighted ? "impression-weighted" : undefined}
							/>
						</div>

						{connected && hasEngineProperty && (
							<div className="monitoring__ai-traffic-chart-grid">
								<div className="monitoring__ai-traffic-kpi-card">
									<h3 className="monitoring__ai-traffic-section-title">
										SERP absorption indicator
									</h3>
									<p className="monitoring__ai-traffic-section-note mb-2">
										Impressions change minus clicks change over the same window.
									</p>
									{data.absorption_indicator ? (
										<>
											<div className="monitoring__ai-traffic-kpi-value-row mt-2">
												<span
													className={`monitoring__ai-traffic-headline-value${
														data.absorption_indicator.points > 10
															? " monitoring__ai-traffic-kpi-value--danger"
															: ""
													}`}
												>
													{data.absorption_indicator.points > 0 ? "+" : ""}
													{data.absorption_indicator.points}
												</span>
												<span className="text-muted">pts</span>
												<TrafficSourceBadge source={engine} />
											</div>
											<div className="monitoring__ai-traffic-kpi-hint">
												impressions{" "}
												{(data.absorption_indicator.impressions_delta_pct ??
													0) > 0
													? "▲"
													: "▼"}
												{Math.abs(
													data.absorption_indicator.impressions_delta_pct ?? 0
												)}
												% · clicks{" "}
												{(data.absorption_indicator.clicks_delta_pct ?? 0) > 0
													? "▲"
													: "▼"}
												{Math.abs(
													data.absorption_indicator.clicks_delta_pct ?? 0
												)}
												%
											</div>
											<div className="monitoring__ai-traffic-banner monitoring__ai-traffic-banner--amber mt-2 mb-0">
												{data.absorption_indicator.note}
											</div>
										</>
									) : (
										<p className="monitoring__ai-traffic-empty mb-0 py-4 text-center">
											Impressions change minus clicks change over the same
											window. Needs a comparison window with impressions on both
											sides.
										</p>
									)}
								</div>
								<div className="monitoring__ai-traffic-kpi-card">
									<h3 className="monitoring__ai-traffic-section-title">
										Branded vs unbranded
									</h3>
									<p className="monitoring__ai-traffic-section-note mb-2">
										Same brand and alias set as the Share-of-Voice scope, so the
										two agree.
									</p>
									<div className="mt-2">
										<BarRow
											label="Unbranded"
											value={brand?.unbranded_clicks}
											total={brand?.attributed_clicks}
											barClass="monitoring__ai-traffic-status-bar-fill--redirect"
											note=" clicks"
										/>
										<BarRow
											label="Branded"
											value={brand?.branded_clicks}
											total={brand?.attributed_clicks}
											barClass="monitoring__ai-traffic-status-bar-fill--ok"
											note=" clicks"
										/>
									</div>
									{brand?.complete === false && (
										<p className="monitoring__ai-traffic-kpi-hint fst-italic">
											Split covers {brand.attributed_clicks} of{" "}
											{brand.total_clicks} clicks — {vendorName} withholds rare
											queries, so the remainder cannot be classified.
										</p>
									)}
									<dl className="monitoring__ai-traffic-search-meta">
										<dt>Device</dt>
										<dd>
											{(data.by_device || []).length
												? data.by_device
														.map((d) => `${d.key} ${fmtNum(d.clicks)}`)
														.join(" · ")
												: "—"}
										</dd>
										<dt>Top countries</dt>
										<dd>
											{(data.by_country || [])
												.slice(0, 4)
												.map((c) => `${c.key} ${fmtNum(c.clicks)}`)
												.join(" · ") || "—"}
										</dd>
										<dt>Queries</dt>
										<dd>
											{fmtNum(data.query_totals?.distinct ?? 0)} distinct ·{" "}
											{data.query_totals?.retention_days ?? "—"}d retained
										</dd>
									</dl>
								</div>
							</div>
						)}

						{connected && hasEngineProperty && (
							<div className="monitoring__ai-traffic-kpi-card">
								<h3 className="monitoring__ai-traffic-section-title">
									Clicks &amp; impressions
								</h3>
								<SearchTrendChart series={data.series} engine={engine} />
							</div>
						)}

						{connected && hasEngineProperty && (
							<div className="monitoring__ai-traffic-chart-grid">
								<div className="monitoring__ai-traffic-kpi-card">
									<h3 className="monitoring__ai-traffic-section-title">
										Top queries
									</h3>
									<p className="monitoring__ai-traffic-section-note mb-2">
										What people actually search. Rare queries are withheld by{" "}
										{vendorName}.
									</p>
									<div className="monitoring__table-scroll">
										<table className="monitoring__table">
											<thead>
												<tr>
													<TrafficTableTh>Query</TrafficTableTh>
													<TrafficTableTh className="monitoring__th-num">
														Clicks
													</TrafficTableTh>
													<TrafficTableTh className="monitoring__th-num">
														Impr.
													</TrafficTableTh>
													<TrafficTableTh className="monitoring__th-num">
														Pos.
													</TrafficTableTh>
												</tr>
											</thead>
											<tbody>
												{(data.top_queries || [])
													.slice(0, inline)
													.map((q) => (
														<tr key={q.query}>
															<td>
																<div className="monitoring__ai-traffic-query-cell">
																	<UrlCopyCell
																		url={q.query}
																		label="Copy query"
																	/>
																	{q.is_branded && (
																		<span className="monitoring__ai-traffic-brand-tag">
																			brand
																		</span>
																	)}
																</div>
															</td>
															<td className="monitoring__td-num">
																{q.clicks}
															</td>
															<td className="monitoring__td-num">
																{fmtNum(q.impressions)}
															</td>
															<td className="monitoring__td-num">
																{q.position ?? "—"}
															</td>
														</tr>
													))}
												{(data.top_queries?.length ?? 0) === 0 && (
													<tr>
														<td
															colSpan={4}
															className="monitoring__ai-traffic-empty text-center py-4"
														>
															{surface === "web"
																? "No query data in this range yet."
																: `No ${activeSurface?.label ?? surface} queries in this range yet.`}
														</td>
													</tr>
												)}
											</tbody>
										</table>
									</div>
									{data.top_queries?.length > inline && (
										<div className="monitoring__ai-traffic-card-footer">
											<Button
												size="sm"
												color="primary"
												className="monitoring__action-btn monitoring__action-btn--primary monitoring__ai-traffic-view-all"
												onClick={() => setQueriesOpen(true)}
											>
												View all {data.top_queries.length}
												{listsTruncated ? "+" : ""} queries
											</Button>
										</div>
									)}
								</div>

								<div className="monitoring__ai-traffic-kpi-card">
									<h3 className="monitoring__ai-traffic-section-title">
										Top pages in search
									</h3>
									<p className="monitoring__ai-traffic-section-note mb-2">
										Normalised with the same page key as AI crawls and referrals,
										so pages line up across sources.
									</p>
									<div className="monitoring__table-scroll">
										<table className="monitoring__table">
											<thead>
												<tr>
													<TrafficTableTh>Page</TrafficTableTh>
													<TrafficTableTh className="monitoring__th-num">
														Clicks
													</TrafficTableTh>
													<TrafficTableTh className="monitoring__th-num">
														Impr.
													</TrafficTableTh>
													<TrafficTableTh className="monitoring__th-num">
														Pos.
													</TrafficTableTh>
												</tr>
											</thead>
											<tbody>
												{(data.top_pages || [])
													.slice(0, inline)
													.map((p) => (
														<tr key={p.url_key}>
															<td>
																<UrlCopyCell
																	url={p.url}
																	display={p.url_key}
																	mono
																/>
															</td>
															<td className="monitoring__td-num">
																{p.clicks}
															</td>
															<td className="monitoring__td-num">
																{fmtNum(p.impressions)}
															</td>
															<td className="monitoring__td-num">
																{p.position ?? "—"}
															</td>
														</tr>
													))}
												{(data.top_pages?.length ?? 0) === 0 && (
													<tr>
														<td
															colSpan={4}
															className="monitoring__ai-traffic-empty text-center py-4"
														>
															{surface === "web"
																? "No page data in this range yet."
																: `No ${activeSurface?.label ?? surface} pages in this range yet.`}
														</td>
													</tr>
												)}
											</tbody>
										</table>
									</div>
									{data.top_pages?.length > inline && (
										<div className="monitoring__ai-traffic-card-footer">
											<Button
												size="sm"
												color="primary"
												className="monitoring__action-btn monitoring__action-btn--primary monitoring__ai-traffic-view-all"
												onClick={() => setPagesOpen(true)}
											>
												View all {data.top_pages.length}
												{listsTruncated ? "+" : ""} pages
											</Button>
										</div>
									)}
								</div>
							</div>
						)}
					</>
				)}
			</div>

			{queriesOpen && data && (
				<TrafficListDialog
					title="Top queries"
					detail={`Ordered by clicks. ${vendorName} withholds rare queries, so this list sums to less than the click total.`}
					rows={data.top_queries || []}
					unit="query"
					pageSize={inline}
					rowKey={(q) => q.query}
					searchText={(q) => q.query}
					copyText={(q) => q.query}
					onClose={() => setQueriesOpen(false)}
					columns={[
						{
							key: "query",
							label: "Query",
							render: (q) => (
								<UrlCopyCell url={q.query} label="Copy query" />
							),
						},
						{
							key: "brand",
							label: "Type",
							render: (q) => (q.is_branded ? "branded" : "unbranded"),
						},
						...(showQueriesIntent
							? [
									{
										key: "intent",
										label: "Intent",
										render: (q) => q.intent ?? "—",
									},
								]
							: []),
						{
							key: "clicks",
							label: "Clicks",
							align: "right",
							render: (q) => q.clicks,
						},
						{
							key: "impr",
							label: "Impr.",
							align: "right",
							render: (q) => q.impressions,
						},
						{
							key: "ctr",
							label: "CTR",
							align: "right",
							render: (q) => (q.ctr == null ? "—" : `${q.ctr}%`),
						},
						{
							key: "pos",
							label: "Pos.",
							align: "right",
							render: (q) => q.position ?? "—",
						},
					]}
				/>
			)}

			{pagesOpen && data && (
				<TrafficListDialog
					title="Top pages in search"
					detail="Ordered by clicks, keyed on the shared page normaliser."
					rows={data.top_pages || []}
					unit="page"
					pageSize={inline}
					rowKey={(p) => p.url_key}
					searchText={(p) => p.url_key}
					copyText={(p) => p.url}
					onClose={() => setPagesOpen(false)}
					columns={[
						{
							key: "page",
							label: "Page",
							render: (p) => (
								<UrlCopyCell url={p.url} display={p.url_key} mono />
							),
						},
						{
							key: "clicks",
							label: "Clicks",
							align: "right",
							render: (p) => p.clicks,
						},
						{
							key: "impr",
							label: "Impr.",
							align: "right",
							render: (p) => p.impressions,
						},
						{
							key: "ctr",
							label: "CTR",
							align: "right",
							render: (p) => (p.ctr == null ? "—" : `${p.ctr}%`),
						},
						{
							key: "pos",
							label: "Pos.",
							align: "right",
							render: (p) => p.position ?? "—",
						},
					]}
				/>
			)}
		</div>
	);
}
